// @ts-nocheck
import React, { useCallback, useState, useEffect } from "react";
import { Box, TextField, Autocomplete, Typography, Tooltip } from "@mui/material";
import BankStatementsSummary from "../underwriting/BankStatementsSummary";
import AdvancesPositions from "../underwriting/AdvancesPositions";
import RecurringPayments from "../underwriting/RecurringPayments";
import SummaryCard from "../underwriting/SummaryCard";
import { get, post } from "requests";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CustomModal from "components/CustomModal";
import MDSnackbar from "components/MDSnackbar";
import { useStoreState } from "easy-peasy";
import FullPageLoader from "components/Loader/FullPage";
import MDTypography from "components/MDTypography";
import StorageUtils from "utils/StorageUtils";
import { Autorenew, PlayCircleFilled } from "@mui/icons-material";

interface SmartScrubTabProps {
  dealApplicationId: number;
  isReadOnly?: boolean;
  onViewTransactions?: (account: { accountnumber: string; monthyear: string }) => void;
  mcaSummary?: any;
  aiSummary?: any;
  setPositionsCount?: (count: number) => void;
  permittedTabs: any;
}

export default function SmartScrubTab({
  dealApplicationId,
  onViewTransactions,
  isReadOnly,
  permittedTabs,
}: SmartScrubTabProps) {
  const [loadingStates, setLoadingStates] = useState({
    bankStatements: false,
    summaryCard: false,
    advancesPositions: false,
  });

  const [hasSmartScrubSummary, setHasSmartScrubSummary] = useState(false);
  const [isProcessingScrub, setIsProcessingScrub] = useState(false);

  const [showSmartScrubModal, setShowSmartScrubModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);


  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [alert, setAlert] = useState<any>(null);
  const [showManualScrub, setShowManualScrub] = useState(false);
  const mediaAttachments = useStoreState((state: any) => state.fileUploads.mediaAttachments);

  useEffect(() => {
    fetchSmartScrubStatusAndSummary();
  }, []);

  useEffect(() => {
    if (Array.isArray(mediaAttachments) && mediaAttachments.length > 0) {
      const statementsFile = mediaAttachments.filter(
        (file: any) => file.category === "Bank Statements"
      );
      setFiles(statementsFile);
    } else {
      setFiles([]);
    }
  }, [mediaAttachments]);

  const fetchSmartScrubStatusAndSummary = async () => {
    try {
      setIsLoading(true)
      const reqResponse: any = await get(`/ocr/requests/${dealApplicationId}`);
      const anyProcessing =
        Array.isArray(reqResponse) &&
        reqResponse.some((req) => (req?.status || "").toLowerCase() === "processing");

      if (anyProcessing) {
        setIsProcessingScrub(true);
        return;
      }
      setIsProcessingScrub(false);
      const allowedSources = ["ocr", "plaid", "internal"];
      const summaryResponse = await get(`/ocr/transactionSummary/${dealApplicationId}`);
      const summaryData = (summaryResponse || []).filter((row: any) =>
        allowedSources.includes((row.source || "").toLowerCase())
      );
      const advancesResponse = await get(`/bankStatements/getAllAdvances/${dealApplicationId}`);
      const advancesData = (advancesResponse || []).filter((row: any) =>
        allowedSources.includes((row.source || "").toLowerCase())
      );
      const hasData = summaryData.length > 0 || advancesData.length > 0;

      setHasSmartScrubSummary(hasData);
    } catch (err) {
      setIsProcessingScrub(false);
      setHasSmartScrubSummary(false);
    } finally{
      setIsLoading(false)
    }
  };

  const setComponentLoading = useCallback((componentName: string, isLoading: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [componentName]: isLoading,
    }));
  }, []);

  const handleFileSelect = (event: any, newValue: any) => {
    setSelectedFiles(newValue.map((option: any) => option.value));
  };

  const runSmartScrub = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setAlert({
        type: "warning",
        message: "Please select at least one bank statement file.",
      });
      return;
    }

    try {
      setIsAnalyzing(true);
      const response = await post(
        "ocr/extractStatements",
        {
          appId: dealApplicationId,
          mediaAttachmentIds: selectedFiles,
        },
        { timeout: 0 }
      );

      const successCount = response?.successCount;
      const failedCount = response?.failedCount;

      if (failedCount === 0 && successCount > 0) {
        setAlert({
          type: "success",
          message: (
            <>
              <strong>Files Accepted</strong>
              <br />
              Track processing status with the <strong>"Info"</strong> button at the top. <br />
              Please <strong>"Refresh"</strong> once processing is completed.
              <br />
            </>
          ),
          timeOut: 30000,
        });
      } else if (failedCount > 0 && successCount > 0) {
        setAlert({
          type: "warning",
          message: (
            <>
              <strong>Only {successCount} file(s) accepted.</strong> <br />
              Track processing status with the <strong>"Info"</strong> button at the top. <br />
              Please <strong>"Refresh"</strong> once processing is completed.
              <br />
            </>
          ),
          timeOut: 30000,
        });
      } else if (failedCount > 0 && successCount === 0) {
        setAlert({
          type: "error",
          message: "Error occurred during submission",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: "Error starting SmartScrub process.",
      });
    } finally {
      setIsAnalyzing(false);
      setShowSmartScrubModal(false);
      setSelectedFiles([]);
      fetchSmartScrubStatusAndSummary();
    }
  };

  const openModal = () => setShowSmartScrubModal(true);
  const closeModal = () => {
    setShowSmartScrubModal(false);
    setSelectedFiles([]);
  };

  if (isProcessingScrub) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        p={3}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <MDBox position="relative" mb={3}>
          <MDBox
            sx={{
              width: 80,
              height: 80,
              border: "4px solid",
              borderColor: "grey.300",
              borderTopColor: "primary.main",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </MDBox>

        <MDTypography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          Processing SmartScrub
        </MDTypography>

        <MDTypography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 400, textAlign: "center", mb: 3 }}
        >
          SmartScrub is analyzing your bank statements. This usually takes a few moments.
        </MDTypography>

        <MDButton variant="contained" color="primary" onClick={fetchSmartScrubStatusAndSummary}>
          Check Again
        </MDButton>
      </MDBox>
    );
  }


  return (
    <div style={isReadOnly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
      {(isAnalyzing || isLoading) && <FullPageLoader />}
      {hasSmartScrubSummary || showManualScrub ? (
        <div>
          <SummaryCard
            dealApplicationId={dealApplicationId}
            allowButton={false}
            permittedTabs={permittedTabs}
            allowSummarySelection={false}
            allowResubmit={!showManualScrub && StorageUtils.isFeatureEnabled('ocr') }
            onResubmit={()=>{setShowSmartScrubModal(true)}}
          />

          <BankStatementsSummary
            source="smartScrub"
            dealApplicationId={dealApplicationId}
            onViewTransactions={onViewTransactions}
            setComponentLoading={setComponentLoading}
          />

          <AdvancesPositions
            source="smartScrub"
            dealApplicationId={dealApplicationId}
            setComponentLoading={setComponentLoading}
          />

          <RecurringPayments
            source="smartScrub"
            dealApplicationId={dealApplicationId}
            setComponentLoading={setComponentLoading}
          />
        </div>
      ) : !isLoading ? (
        <MDBox display="flex" flexDirection="row" justifyContent="flex-end" p={1} gap={2}>
          {StorageUtils.isFeatureEnabled("ocr") && (
            <MDButton
              variant="contained"
              color="primary"
              onClick={openModal}
              disabled={isAnalyzing}
              sx={{
                mt: 2,
                px: 3,
                py: 1,
                borderRadius: 1,
                fontWeight: "bold",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
              startIcon={
                isAnalyzing ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
                      stroke="currentColor"
                      strokeWidth={2}
                      fill="none"
                    />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      stroke="currentColor"
                      strokeWidth={2}
                      fill="currentColor"
                    />
                    <path
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth={2}
                      fill="none"
                    />
                  </svg>
                )
              }
            >
              {isAnalyzing ? "Starting..." : "Run SmartScrub"}
            </MDButton>
          )}

          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setShowManualScrub(true)}
            sx={{
              mt: 2,
              px: 3,
              py: 1,
              borderRadius: 1,
              fontWeight: "bold",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
            startIcon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 12H16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 16V8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            Scrub Manually
          </MDButton>
        </MDBox>
      ) : null }

      <CustomModal
        open={showSmartScrubModal}
        onClose={closeModal}
        title="Upload Bank Statements (SmartScrub)"
        cancelText="Cancel"
        submitText="Submit"
        onCancel={closeModal}
        onSubmit={runSmartScrub}
        maxWidth="sm"
        fullWidth
      >
        <MDBox display="flex" flexDirection="column">
          <Autocomplete
            multiple
            id="smartScrub-file-select"
            options={files.map((file) => ({
              value: file.id,
              label: file.name,
            }))}
            getOptionLabel={(option) => option.label}
            value={files
              .filter((file) => selectedFiles.includes(file.id))
              .map((file) => ({
                value: file.id,
                label: file.name,
              }))}
            onChange={handleFileSelect}
            disableCloseOnSelect
            renderInput={(params) => <TextField {...params} label="Select Statement Files" />}
            sx={{ mt: 2, mb: 2 }}
          />
        </MDBox>
      </CustomModal>

      {alert && (
        <MDSnackbar
          color={alert?.type}
          icon="notifications"
          title={alert?.type}
          content={alert?.message}
          open={alert !== null}
          close={() => setAlert(null)}
        />
      )}
    </div>
  );
}
